// app/src/lib/cashflow/accounts.js — user-managed tags on Plaid accounts.
//
// User-set "purpose" label on a connected bank account
// (operating / payroll / savings / other). Never derived from Plaid.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';

const ALLOWED_PURPOSES = new Set(['operating', 'payroll', 'savings', 'other']);

export async function setPurpose(plaidAccountId, purpose) {
  const userId = await requireUserId();
  const acct = await db.plaidAccounts.get(plaidAccountId);
  if (!acct || acct.userId !== userId) throw new Error('account: not found');
  if (!ALLOWED_PURPOSES.has(purpose)) throw new Error('account: invalid purpose');
  acct.purpose = purpose;
  await db.plaidAccounts.put(acct);
  return acct;
}

// ── Cash vs. credit ────────────────────────────────────────────────────────
// The cash-based reports (cash position, owner-draw / retained income,
// taxes / rent / insurance / payroll) model money leaving the DEPOSITORY
// (checking / savings) accounts. A credit-card CHARGE is not cash leaving the
// bank — the cash leaves later, as the card PAYMENT from checking — so counting
// card charges in those reports double-counts spend against that payment (and
// tanks "retained income" while the checking balance is untouched). Card
// spending has its own home: the Spending tab.

/** Set of the user's CREDIT-card account ids (empty when no card is linked). */
export async function creditAccountIds(userId) {
  const uid = userId || await requireUserId();
  const accts = await db.plaidAccounts.where('userId').equals(uid).toArray();
  return new Set(accts.filter((a) => a.type === 'credit').map((a) => a.plaidAccountId));
}

/**
 * Drop credit-card-account transactions from a tx list. No-op when the user has
 * no cards. Everything else — checking, savings, CSV/upload, unknown accounts —
 * is kept as cash, INCLUDING the card payment that clears from checking (which
 * is the real cash expense the card spending nets out to).
 */
export function withoutCreditTxs(txs, creditSet) {
  if (!creditSet || creditSet.size === 0) return txs || [];
  return (txs || []).filter((t) => !creditSet.has(t.plaidAccountId));
}

// ── Credit-card spend model (owner-confirmed) ───────────────────────────────
// Card CHARGES are the spend, counted as a cost on the DAY CHARGED — the
// Spending tab, the Sankey, and the Today / owner-draw rollups all read them
// that way. The BILL PAYMENT is just the settlement transfer of spend already
// counted (a checking debit mirrored by a credit on the card), so every cash
// and cost view must skip BOTH sides or the same dollars count twice.
// Detection is the Plaid PFC primary ('LOAN_PAYMENTS'), the same signal
// ownerDraw uses to rule card payments out of draws.

/** True for either side of a credit-card bill payment (checking debit OR the
 *  mirror credit on the card) — the settlement transfer cash views skip. */
export function isCardPayment(tx) {
  return !!tx && tx.category === 'LOAN_PAYMENTS';
}
